import React from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {sb} from '../lib/supabase.js';

import {navigateToProcessingRoute, processingSourceRoute} from '../lib/processingNav.js';
import {fmtMDY as fmt, fmtMDY as fmtS, todayISO} from '../lib/dateUtils.js';

import {recordFieldRowClass, recordFieldLabel, recordControl, recordTextarea} from '../shared/recordPageControls.jsx';
import {
  buildCycleSeqMap,
  cycleLabel,
  calcBreedingTimeline,
  pigTransfersForSub,
  pigTransfersForBatch,
  pigTripPigsForSub,
  pigMortalityForSub,
  pigMortalityForBatch,
  calcAgeRange as libCalcAgeRange,
  parseLiveWeights,
  processingTripWithResolvedWeights,
  tripTotalLive,
  tripYield,
  computeSubCurrentCount,
  computeBatchCurrentCount,
  resolveTripBreeders,
  tripTotalAnimals,
  tripTotalHanging,
  tripBreederHangingTotal,
  breederLiveWeight,
  findBreederTripAttachment,
} from '../lib/pig.js';
import {programDotStyle} from '../lib/programColors.js';
import {
  PLANNED_TRIP_MIN_SIZE,
  PLANNED_TRIP_TARGET_WEIGHT_LBS,
  PLANNED_TRIP_OVER_WEIGHT_WARN_LBS,
  buildFarrowingAgeDistribution,
  projectFarrowingAgeWindow,
  recalculateProjections,
} from '../lib/pigForecast.js';
import {ANIMAL_ICON_KEYS} from '../lib/plannerIcons.js';
import {pigBatchProcessingStatusLabel} from '../lib/processingStatusDisplay.js';
// The `no-unused-vars` disables these five imports used to carry are deleted,
// not retargeted: the core rule is off for TypeScript files and
// @typescript-eslint/no-unused-vars resolves JSX references, so the directives
// themselves reported as unused.
import PlannerIcon from '../components/PlannerIcon.jsx';
import InlineNotice from '../shared/InlineNotice.jsx';
import PigConflictNotice from './PigConflictNotice.js';
import Badge from '../shared/Badge.jsx';
import Button from '../shared/Button.js';
import Collapsible from '../shared/Collapsible.js';
import Modal, {ModalFooter} from '../shared/Modal.js';
import OverflowMenu from '../shared/OverflowMenu.js';
import RecordCollaborationSection from '../shared/RecordCollaborationSection.jsx';
import RecordSequenceNav from '../shared/RecordSequenceNav.tsx';
import {RecordPageFrame, RecordPageBody, RecordBackLink, RecordTitle} from '../shared/RecordPageShell.jsx';
import {useAuth} from '../contexts/AuthContext.jsx';
import {usePig} from '../contexts/PigContext.jsx';
import type {
  PigBreederRow,
  PigTransferSource,
  PigBreedingCycle,
  PigFarrowingRecord,
  PigFeederGroup,
  PigProcessingTrip,
  PigSubBatch,
} from '../lib/pig.js';
import type {RecordSeqEntry, RecordSeqId} from '../lib/recordSequence.js';
import type {StoredPlannedTrip} from '../lib/pigForecast.js';
import type {Notice} from '../shared/InlineNotice.js';
import type {ProcessingStatusSource, PigProcessingMetrics} from '../lib/processingStatusDisplay.js';
import type {usePigMortality} from './usePigMortality.js';
import type {usePigSubBatches} from './usePigSubBatches.js';
import type {usePigPlannedTrips} from './usePigPlannedTrips.js';
import type {usePigProcessingTrips} from './usePigProcessingTrips.js';
import Header from '../shared/Header.tsx';
import './pigBatchRedesign.css';

function BatchActionIcon({kind}: {kind: 'add' | 'edit' | 'delete' | 'reopen'}) {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {kind === 'add' ? (
        <path d="M12 5v14M5 12h14" />
      ) : kind === 'delete' ? (
        <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
      ) : kind === 'edit' ? (
        <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
      ) : (
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5" />
      )}
    </svg>
  );
}

// PigBatchPage — the single-batch record workspace (CP11 file extraction from
// PigBatchesView). Pure relocation: the card JSX, mortality modal, and the
// RecordCollaborationSection mount are byte-identical to the prior inline
// render. Workflow logic still lives in the hooks (called by PigBatchesView and
// threaded in via the `view` bundle); shared PigContext is consumed directly
// here. PigBatchesView remains the hub/router and renders this only when
// recordGroup exists.
// ── Props ───────────────────────────────────────────────────────────────────

// A pig_dailys row as this page reads it. DailysRecentContext / PigBatchesView
// hold the rows untyped — App loads them with `select('*')` — and these are the
// columns the feed and report-day maths touches.
type PigBatchDailyRow = {
  date?: string | null;
  submitted_at?: string | null;
  batch_label?: string | null;
  batch_id?: string | null;
  feed_lbs?: number | string | null;
  pig_count?: number | string | null;
};

// The `view` bundle PigBatchesView assembles: the fields it owns itself plus
// the four hook results it spreads in. Those four are taken as ReturnType of
// the hooks rather than re-listed, so this page cannot drift from what the
// hooks actually return. The imports for them are type-only and erase.
//
// The last five are pig.batches.core's, not PigBatchesView's own: they are the
// blobs this page used to reach usePig() for. They travel here because the
// record surface is rendered INLINE by that component off the SAME resource
// read, so a second useRouteResource call would be a second owner of one
// render. The shapes are named at this boundary rather than inferred, because
// the catalog hands them over as its generic row type.
type PigBatchPageViewOwn = {
  // Shaped like src/pig/PigEntry.tsx's TimerRef, which is where the ref comes
  // from: App creates it with useRef(null). The pig hooks declare the same ref
  // with `| undefined` instead; the two disagree only about the empty value.
  subAutoSaveTimer: {current: ReturnType<typeof setTimeout> | null};
  isManager: boolean;
  notice: PigBatchNotice | null;
  setNotice: (notice: PigBatchNotice | null) => void;
  effectiveAdgLbsPerDay: number | null;
  dailysForName: (name: string | null | undefined) => PigBatchDailyRow[];
  archiveBatch: (batchId: string) => void;
  unarchiveBatch: (batchId: string) => void;
  breedingCycles: PigBreedingCycle[];
  breeders: PigBreederRow[];
  farrowingRecs: PigFarrowingRecord[];
  feederGroups: PigBatchGroup[];
  // The two boar display names, as pig.batches.core resolves them: the stored
  // ppp-boars-v1 blob spread onto DEFAULT_BOAR_NAMES, so both keys are always
  // present and the cycle strip's `boarNames.boar1` cannot be undefined.
  boarNames: {boar1: string; boar2: string};
  processingTripOrdinals: Record<string, number>;
  // R25: the batch's own editor, owned by PigBatchesView (the same field
  // block, closeFeederForm and deleteFeeder the modal used) and rendered in
  // place on the Batch Details card.
  feederEditor: {
    editingId: string | null;
    renderFields: () => React.ReactNode;
    conflictNotice: React.ReactNode;
    close: () => Promise<void>;
    remove: (id: string) => void;
    busy: boolean;
  };
};

// The notice shape both this page and the hooks use.
type PigBatchNotice = {kind: string; message: string};

type PigBatchPageView = PigBatchPageViewOwn &
  ReturnType<typeof usePigMortality> &
  ReturnType<typeof usePigSubBatches> &
  ReturnType<typeof usePigPlannedTrips> &
  ReturnType<typeof usePigProcessingTrips>;

// A feeder batch as this record page reads it. src/lib/pig.ts's PigFeederGroup
// declares the columns its own helpers use; `notes` and `perLbFeedCost` are the
// two the batch card additionally shows, and PigFeederGroup leaves both to its
// index signature.
//
// `id` is REQUIRED here while PigFeederGroup makes it optional, and the same
// goes for the sub-batch and trip rows below. That is not a widening: this page
// is mounted only for a group PigBatchesView already found by id, and every
// handler on it (archiveBatch(g.id), archiveSubBatch(g.id, sb.id),
// deletePlannedTripById(g.id, t.id), …) passes those ids unguarded. Declaring
// them required records the assumption the code already makes instead of
// scattering an assertion over every call site.
type PigBatchGroup = PigFeederGroup & {
  id: string;
  // Required for the same reason as `id`: the page's title and its
  // RecordCollaborationSection entity label both read it unguarded, and that
  // mount is pinned byte-for-byte by a raw-source guard.
  batchName: string;
  notes?: string | null;
  perLbFeedCost?: number | string | null;
};

// A sub-batch as the record page renders it: PigSubBatch plus the notes column
// the edit form binds.
type PigBatchSub = PigSubBatch & {id: string; notes?: string | null};

// An actual processing trip. PigProcessingTrip names pigCount / liveWeights /
// hangingWeight / subAttributions; the card also reads the date and the notes.
type PigBatchTrip = PigProcessingTrip & {id: string; date?: string | null; notes?: string | null};

// A planned trip row. src/lib/pigForecast.ts's StoredPlannedTrip makes every
// key optional; the chain UI keys, locks, and moves every row by id.
type PigBatchPlannedTrip = StoredPlannedTrip & {id: string};

// The quiet text-button look the trip row's breeding-pig controls share.

// What usePigProcessingTrips' tripSourceSummary actually builds, and what the
// inline fallback literal for an unsaved trip supplies. src/lib/pig.ts's
// TripSourceSummary — the hook's declared return type — leaves `weights` and
// the count maps `unknown`, because the yield helpers only ever coerce them.
// This page RENDERS them, so it names the concrete shape the hook returns.
type PigTripSourceSummary = {
  weights: number[];
  count: number;
  totalLive: number;
  avgLive: number | null;
  counts: Record<string, number>;
};

// The feeder-batch edit form as this page fills it. PigContext seeds the state
// from a ten-key literal that has no `feedAllocatedToTransfers`; this opener has
// always written an eleventh key, and PigBatchesView's save path reads it. The
// context's seed type is therefore narrower than the state, which is why the
// setter takes the cast below rather than the object being trimmed.
type PigBatchFeederForm = {
  batchName: string;
  cycleId: string;
  giltCount: number;
  boarCount: number;
  startDate: string;
  originalPigCount: number;
  perLbFeedCost: number;
  legacyFeedLbs: number;
  feedAllocatedToTransfers: number;
  notes: string;
  status: string;
};
type PigContextFeederForm = Omit<PigBatchFeederForm, 'feedAllocatedToTransfers'>;

// The processing-trip form, as PigContext seeds it and this page refills it.
type PigBatchTripForm = {
  date: string;
  pigCount: number;
  liveWeights: string;
  hangingWeight: number;
  notes: string;
};

// One trip's projected finish-weight band, keyed by trip id. `source` and
// `hasActual` come straight out of projectFarrowingAgeWindow.
type ProcessingForecast = {
  min: number;
  max: number;
  avg: number;
  source: string | null;
  hasActual: boolean;
};

type PigBatchPageProps = {
  group: PigBatchGroup;
  view: PigBatchPageView;
  recordSeq?: RecordSeqEntry[] | null;
  recordId?: RecordSeqId;
  onNavigateSeq: (id: RecordSeqId) => void;
  onBack: () => void;
  // PigBatchesView passes Header through even though this file imports Header
  // itself and never reads the prop. Declared so the existing mount type-checks
  // without the mount being changed; nothing here reads it.
  Header?: unknown;
};

export default function PigBatchPage({
  group,
  view,
  recordSeq = null,
  recordId = null,
  onNavigateSeq,
  onBack,
}: PigBatchPageProps) {
  const navigate = useNavigate();
  const location = useLocation() as {pathname: string; search: string};
  const {authState} = useAuth();
  // ONLY THE FORM STATE COMES FROM usePig NOW. The five former-eager values
  // this page used to destructure here — breedingCycles, breeders,
  // farrowingRecs, feederGroups and boarNames — are pig.batches.core's, read
  // once by PigBatchesView above the recordMode branch and handed over on the
  // `view` bundle. Re-reading them from the provider here would leave the
  // `pigbatches` retirement in tests/fixtures/former-eager-dependencies.json a
  // false claim while every number on this page still came out of App's boot.
  const {
    setFeederForm,
    setOriginalFeederForm,
    setShowFeederForm,
    setEditFeederId,
    activeTripBatchId,
    setActiveTripBatchId,
    tripForm,
    setTripForm: setTripFormRaw,
    editTripId: editTripIdRaw,
    setEditTripId,
  } = usePig();
  const {breedingCycles, breeders, farrowingRecs, feederGroups, boarNames, processingTripOrdinals} = view;
  const {feederEditor} = view;
  // PigContext types the open-trip id `unknown` — it stores whatever id a view
  // hands it — and this page only ever sets it to a trip's id or null.
  const editTripId = editTripIdRaw as string | null;
  // The trip form. PigContext seeds it from a five-key literal whose numeric
  // fields are 0, and the openers below copy a stored trip's values straight in,
  // which is what the setter narrowing records. See PigTripForm.
  const setTripForm = setTripFormRaw as unknown as React.Dispatch<React.SetStateAction<PigBatchTripForm>>;
  const {
    subAutoSaveTimer,
    isManager,
    notice,
    setNotice,
    effectiveAdgLbsPerDay,
    dailysForName,
    archiveBatch,
    unarchiveBatch,
    // mortality
    mortalityModal,
    setMortalityModal,
    mortalityForm,
    setMortalityForm,
    mortalityBusy,
    expandedMortality,
    setExpandedMortality,
    openMortalityModal,
    saveMortality,
    deleteMortality,
    // sub-batches
    showSubForm,
    setShowSubForm,
    subForm,
    setSubForm,
    editSubId,
    subConflict,
    setEditSubId,
    archiveSubBatch,
    unarchiveSubBatch,
    updSub,
    closeSubForm,
    deleteSubBatch,
    // planned trips
    editingPlannedTripId,
    setEditingPlannedTripId,
    editingPlannedTripDate,
    setEditingPlannedTripDate,
    addingTripFor,
    setAddingTripFor,
    addingTripDate,
    setAddingTripDate,
    addingTripCount,
    setAddingTripCount,
    addingTripError,
    setAddingTripError,
    plannedTripLocks,
    unlockingTripId,
    setUnlockingTripId,
    isChainLocked,
    lockPlannedTrip,
    unlockPlannedTrip,
    setPlannedTripDateById,
    shiftPlannedTripDateById,
    movePlannedTripPigsById,
    addPlannedTripById,
    deletePlannedTripById,
    // processing trips
    tripRefreshPending,
    retryTripRefresh,
    tripSourceCounts,
    tripSourceSummary,
    updTrip,
    closeTripForm,
    deleteTrip,
    attachBreederToTrip,
    detachBreederFromTrip,
    setBreederHangingWeight,
  } = view;

  // ── "Add breeding pig" to a processing trip ───────────────────────────────
  // Which trip's picker is open (null = closed). A trip is chosen explicitly
  // and never inferred: nothing here picks a "nearby" trip for the operator.
  const [breederPickerTripId, setBreederPickerTripId] = React.useState<string | null>(null);
  const [breederPickerSearch, setBreederPickerSearch] = React.useState('');
  const [breederPickerBusy, setBreederPickerBusy] = React.useState(false);
  const [breederTripNotice, setBreederTripNotice] = React.useState<{kind: 'error' | 'ok'; message: string} | null>(
    null,
  );

  function openBreederPicker(tripId: string) {
    setBreederTripNotice(null);
    setBreederPickerSearch('');
    setBreederPickerTripId(tripId);
  }

  async function onAttachBreeder(groupId: string, tripId: string, breederId: string, label: string) {
    setBreederPickerBusy(true);
    const res = await attachBreederToTrip(groupId, tripId, breederId, label);
    setBreederPickerBusy(false);
    if (!res.ok) {
      setBreederTripNotice({kind: 'error', message: res.message});
      return;
    }
    setBreederPickerTripId(null);
    setBreederTripNotice({
      kind: 'ok',
      message: res.alreadyAttached
        ? `#${label} was already on this trip.`
        : `Added #${label} to this trip. Her status was not changed.`,
    });
  }

  // Which attached breeder's carcass weight is being edited, and the draft.
  const [breederWeightEdit, setBreederWeightEdit] = React.useState<{tripId: string; breederId: string} | null>(null);
  const [breederWeightDraft, setBreederWeightDraft] = React.useState('');

  async function onSaveBreederWeight(groupId: string, tripId: string, breederId: string) {
    const raw = breederWeightDraft.trim();
    const parsed = raw === '' ? null : parseFloat(raw);
    if (parsed !== null && !(parsed > 0)) {
      setBreederTripNotice({
        kind: 'error',
        message: 'Enter a hanging weight greater than zero, or leave it blank to clear it.',
      });
      return;
    }
    setBreederPickerBusy(true);
    const res = await setBreederHangingWeight(groupId, tripId, breederId, parsed);
    setBreederPickerBusy(false);
    if (!res.ok) {
      setBreederTripNotice({kind: 'error', message: res.message});
      return;
    }
    setBreederWeightEdit(null);
    setBreederTripNotice({
      kind: 'ok',
      message:
        parsed === null
          ? 'Cleared that breeding pig' + String.fromCharCode(8217) + 's hanging weight.'
          : 'Recorded ' + parsed + ' lbs hanging for that breeding pig. The batch hanging weight was not changed.',
    });
  }

  async function onDetachBreeder(groupId: string, tripId: string, breederId: string, label: string) {
    setBreederPickerBusy(true);
    const res = await detachBreederFromTrip(groupId, tripId, breederId, label);
    setBreederPickerBusy(false);
    setBreederTripNotice(
      res.ok
        ? {kind: 'ok', message: `Removed #${label} from this trip. Her status was not changed.`}
        : {kind: 'error', message: res.message},
    );
  }

  // Exact-trip deep-link focus: /pig/batches/<groupId>?trip=<tripId> (the
  // Processing drawer's "View pig trip" back-link, processingSourceLink.js)
  // scrolls to and briefly outlines the matching planned OR actual trip
  // element (both carry data-pig-trip={t.id} anchors). This mirrors the
  // comment deep-link pattern on the other record pages (#comment-<id>:
  // setTimeout + scrollIntoView block:'center'); there is no shared flash
  // primitive in the app, so the pulse is a temporary outline — outline is
  // not part of any React-managed style prop here, so re-renders can't
  // clobber or resurrect it.
  const focusTripId = React.useMemo(() => {
    try {
      return new URLSearchParams(location.search).get('trip');
    } catch (_e) {
      return null;
    }
  }, [location.search]);
  const focusTripDoneRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!focusTripId || focusTripDoneRef.current === focusTripId) return;
    const timer = setTimeout(() => {
      const esc =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(focusTripId) : focusTripId;
      const el = document.querySelector<HTMLElement>(`[data-pig-trip="${esc}"]`);
      if (!el) return; // not rendered yet — group/planned data still settling; retried via deps
      focusTripDoneRef.current = focusTripId;
      if (el.scrollIntoView) el.scrollIntoView({behavior: 'smooth', block: 'center'});
      el.style.outline = '2px solid var(--brand)';
      el.style.outlineOffset = '2px';
      setTimeout(() => {
        el.style.outline = '';
        el.style.outlineOffset = '';
      }, 2400);
    }, 200);
    return () => clearTimeout(timer);
  }, [focusTripId, group]);

  // Local view-scoped derivations (were inline in PigBatchesView).
  // CP0 WI-4: batch/sub-batch status -> <Badge>; pig active display derives
  // from started/current head because future placeholders are also stored active.
  const statusLabel = (record: ProcessingStatusSource, metrics: PigProcessingMetrics) =>
    pigBatchProcessingStatusLabel(record, metrics);

  const cycleSeqMap = buildCycleSeqMap(breedingCycles);
  function calcAgeRange(cycleId: string | null | undefined, asOfDate?: Date | null) {
    return libCalcAgeRange(cycleId, asOfDate, breedingCycles, farrowingRecs);
  }

  // R25: the Batch Details card's Edit. The seed the header Edit button used,
  // unchanged: it opens the one feeder editor PigBatchesView owns.
  function openBatchEditor(g: PigBatchGroup) {
    setNotice(null);
    const f: PigBatchFeederForm = {
      batchName: g.batchName as string,
      cycleId: g.cycleId || '',
      giltCount: g.giltCount as number,
      boarCount: g.boarCount as number,
      startDate: g.startDate || '',
      originalPigCount: (g.originalPigCount || 0) as number,
      perLbFeedCost: (g.perLbFeedCost || 0) as number,
      legacyFeedLbs: (g.legacyFeedLbs || 0) as number,
      feedAllocatedToTransfers: (g.feedAllocatedToTransfers || 0) as number,
      notes: g.notes || '',
      status: g.status as string,
    };
    setFeederForm(f as unknown as PigContextFeederForm);
    setOriginalFeederForm(f);
    setEditFeederId(g.id);
    setShowFeederForm(true);
    setShowSubForm(null);
  }

  return (
    <RecordPageFrame Header={Header} data-pig-batch-record-frame="true">
      {/* Mortality entry modal — overlay across the page */}
      {mortalityModal &&
        (() => {
          const target = feederGroups.find((g) => g.id === mortalityModal.batchId);
          if (!target) return null;
          const subs = (target.subBatches || []).filter((s) => s.status === 'active');
          return (
            <Modal
              title="Record Mortality"
              size="sm"
              onClose={() => {
                if (mortalityBusy) return;
                setNotice(null);
                setMortalityModal(null);
              }}
              footer={
                <ModalFooter
                  primaryLabel="Save Mortality"
                  onPrimary={saveMortality}
                  busy={mortalityBusy}
                  primaryDisabled={!mortalityForm.count}
                  cancelDisabled={mortalityBusy}
                />
              }
            >
              <p style={{color: 'var(--text-2)'}}>{target.batchName}</p>
              <div style={{padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12}}>
                <InlineNotice notice={notice as Notice | null} onDismiss={() => setNotice(null)} />
                <div className={recordFieldRowClass}>
                  <span style={recordFieldLabel}>Sub-Batch</span>
                  <select
                    value={mortalityForm.sub_batch_id}
                    onChange={(e) => setMortalityForm({...mortalityForm, sub_batch_id: e.target.value})}
                    style={recordControl}
                  >
                    <option value="">{'— Whole batch (no sub) —'}</option>
                    {subs.map((s) => (
                      <option key={s.id} value={s.id as string}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={recordFieldRowClass}>
                  <span style={recordFieldLabel}>{'Count *'}</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={mortalityForm.count}
                    onChange={(e) => setMortalityForm({...mortalityForm, count: e.target.value})}
                    placeholder="e.g. 1"
                    style={recordControl}
                  />
                </div>
                <div className={recordFieldRowClass}>
                  <span style={recordFieldLabel}>Comment / Cause</span>
                  <textarea
                    value={mortalityForm.comment}
                    onChange={(e) => setMortalityForm({...mortalityForm, comment: e.target.value})}
                    rows={2}
                    placeholder="e.g. found dead in pen, suspected respiratory"
                    style={recordTextarea}
                  />
                </div>
                <div style={{fontSize: 11, color: 'var(--ink-muted)'}}>
                  {'Stamped: ' +
                    todayISO() +
                    ' · ' +
                    ((authState && authState.user && authState.user.email) || 'unknown')}
                </div>
              </div>
            </Modal>
          );
        })()}
      {/* Pig batch owns the standard record-page chrome stack here: Back link,
          sequence navigation, title, loaded body, and collaboration section. */}
      <RecordPageBody maxWidth={960} className="pig-batch-record" data-pig-batch-record-loaded="true">
        <div className="pig-batch-record__nav">
          <RecordBackLink label="Back to Pig Batches" onBack={onBack} />
          <RecordSequenceNav
            seq={recordSeq}
            currentId={recordId || group.id}
            onNavigate={onNavigateSeq}
            layout="inline"
          />
        </div>
        {[group].map((g) => {
          const cycle = breedingCycles.find((c) => c.id === g.cycleId);
          const tl = cycle ? calcBreedingTimeline(cycle.exposureStart) : null;
          const trips = (g.processingTrips || []) as PigBatchTrip[];
          const tripSourceOptions = {tripSourceSummary};
          const tripWithActualWeights = (trip: PigProcessingTrip) =>
            processingTripWithResolvedWeights(trip, tripSourceOptions);
          const totalLive = trips.reduce((s, t) => s + tripTotalLive(t, tripSourceOptions), 0);
          const totalHang = trips.reduce((s, t) => s + (parseFloat(t.hangingWeight as string) || 0), 0);
          // Carcass yield % only counts trips that have a hanging weight
          // entered. Otherwise a trip with no hanging data drags the
          // denominator (live wt) down without contributing to the
          // numerator, making the % look artificially low.
          const tripsWithHang = trips.filter((t) => (parseFloat(t.hangingWeight as string) || 0) > 0);
          const yieldHang = tripsWithHang.reduce((s, t) => s + (parseFloat(t.hangingWeight as string) || 0), 0);
          const yieldLive = tripsWithHang.reduce((s, t) => s + tripTotalLive(t, tripSourceOptions), 0);
          const overallYield = yieldLive > 0 && yieldHang > 0 ? Math.round((yieldHang / yieldLive) * 1000) / 10 : null;
          // Sub-Batches
          const subBatches = (g.subBatches || []) as PigBatchSub[];
          const hasSubBatches = subBatches.length > 0;

          // Feed matching by label (case-insensitive) for parent batch
          const batchDailys = hasSubBatches ? [] : dailysForName(g.batchName);
          const dailyFeedTotal = batchDailys.reduce((s, d) => s + (parseFloat(d.feed_lbs as string) || 0), 0);
          const legacyFeed = parseFloat(g.legacyFeedLbs as string) || parseFloat(g.totalFeedLbs as string) || 0;

          // Per-sub ledger metrics. Started counts come from stored fields
          // (no longer mutated by transfers). Transfers + trip attribution
          // + mortality are derived from audit logs. Sub adjusted feed
          // subtracts the per-sub transfer credit (sourced from breeders[],
          // not the parent-aggregate g.feedAllocatedToTransfers, so sub
          // and parent reconcile cleanly).
          const subFeedTotals = subBatches.map((sb) => {
            const sd = dailysForName(sb.name);
            const rawFeedSub =
              sd.reduce((s, d) => s + (parseFloat(d.feed_lbs as string) || 0), 0) +
              (parseFloat(sb.legacyFeedLbs as string) || 0);
            const transfers = pigTransfersForSub(breeders, g.batchName, sb.name);
            const tripPigs = pigTripPigsForSub(g.processingTrips || [], sb.id, {
              ...tripSourceOptions,
              subName: sb.name,
            });
            const mortality = pigMortalityForSub(g, sb.name);
            const started = (parseInt(sb.giltCount as string) || 0) + (parseInt(sb.boarCount as string) || 0);
            // Processed-aware current via the shared lib helper so hub + future
            // record page derive these identically.
            const currentCount = computeSubCurrentCount(g, sb, breeders, tripSourceOptions);
            const adjustedFeed = Math.max(0, rawFeedSub - transfers.feedAllocLbs);
            return {
              sb,
              dailys: sd,
              started,
              rawFeed: rawFeedSub,
              transferFeedCredit: transfers.feedAllocLbs,
              adjustedFeed,
              feedTotal: adjustedFeed, // back-compat alias
              transferCount: transfers.count,
              tripPigs,
              mortality,
              currentCount,
            };
          });
          const subAdjustedFeedTotal = subFeedTotals.reduce((s, sf) => s + sf.adjustedFeed, 0);
          const subRawFeedTotal = subFeedTotals.reduce((s, sf) => s + sf.rawFeed, 0);
          const rawFeed = hasSubBatches ? subRawFeedTotal + legacyFeed : dailyFeedTotal + legacyFeed;
          // Parent aggregate transfer credit: prefer sum-of-subs (canonical
          // from breeders[]); fall back to g.feedAllocatedToTransfers for
          // parent-only batches that never had subs.
          const subTransferFeedTotal = subFeedTotals.reduce((s, sf) => s + sf.transferFeedCredit, 0);
          const parentTransferAgg = pigTransfersForBatch(breeders, g.batchName);
          const feedAllocatedOut = hasSubBatches
            ? subTransferFeedTotal
            : parentTransferAgg.feedAllocLbs || parseFloat(g.feedAllocatedToTransfers as string) || 0;
          const totalFeed = Math.max(0, rawFeed - feedAllocatedOut);

          // Current pig count: ledger-derived for batches with subs;
          // latest-daily fallback for parent-only batches (no sub
          // attribution available without weigh_in linkage).
          const sortedDailys = hasSubBatches
            ? subFeedTotals
                .flatMap((sf) => sf.dailys)
                .sort(
                  (a, b) =>
                    (b.date as string).localeCompare(a.date as string) ||
                    b.submitted_at?.localeCompare(a.submitted_at || '') ||
                    0,
                )
            : [...batchDailys].sort(
                (a, b) =>
                  (b.date as string).localeCompare(a.date as string) ||
                  b.submitted_at?.localeCompare(a.submitted_at || '') ||
                  0,
              );
          const latestDaily = sortedDailys[0] || null;
          // Ledger-derived current count via the shared lib helper (sums
          // active sub currents when sub-batched; parent-only ledger with a
          // latest-daily fallback otherwise). One source for hub + record page.
          const currentPigCount = computeBatchCurrentCount(g, breeders, {
            latestDailyPigCount: (latestDaily?.pig_count as number) ?? null,
            tripSourceSummary,
          });
          // Freeze age once the batch is empty AND has at least one
          // processor trip — pin the reference date to the latest trip
          // so age stops advancing while the batch lingers archived.
          // Mortality/transfer-only emptying does NOT freeze (no trip
          // date to pin to). If a trip is later edited away and current
          // > 0 again, age resumes using today.
          const latestTripDate =
            trips
              .map((t) => (typeof t?.date === 'string' && t.date ? t.date : null))
              .filter(Boolean)
              .sort()
              .slice(-1)[0] || null;
          const ageRange =
            currentPigCount === 0 && latestTripDate
              ? calcAgeRange(g.cycleId, new Date(latestTripDate + 'T12:00:00'))
              : calcAgeRange(g.cycleId);
          // Started count for denominators: sum-of-subs (for hasSubBatches)
          // or parent stored gilts+boars. Both are now stable across
          // transfers — they reflect what entered the batch.
          const originalPigCount = hasSubBatches
            ? subFeedTotals.reduce((s, sf) => s + sf.started, 0)
            : (parseInt(g.giltCount as string) || 0) + (parseInt(g.boarCount as string) || 0) ||
              parseInt(g.originalPigCount as string) ||
              0;
          const batchStatusMetrics = {started: originalPigCount, current: currentPigCount};
          // Feed Cost
          const perLbCost = parseFloat(g.perLbFeedCost as string) || 0;
          const totalFeedCost = totalFeed > 0 && perLbCost > 0 ? totalFeed * perLbCost : null;
          // Feed conversion ratio = total feed / total live weight produced.
          // Standard FCR definition (lbs feed per lb live weight). The old
          // hybrid (avg-feed-per-pig / avg-live-weight) drifted whenever
          // original-count and pigs-processed weren't equal.
          const pigsProcessed = trips.reduce(
            (s, t) => s + (parseInt(tripWithActualWeights(t).pigCount as unknown as string) || 0),
            0,
          );
          const feedConversion =
            totalFeed > 0 && totalLive > 0 ? Math.round((totalFeed / totalLive) * 100) / 100 : null;
          const showTripForm = activeTripBatchId === g.id;
          const farrowPct = ageRange.total > 0 ? Math.round((ageRange.count / ageRange.total) * 100) : 0;
          const processingForecastByTripId = (() => {
            if (!g.cycleId || effectiveAdgLbsPerDay == null) return {};
            const out: Record<string, ProcessingForecast> = {};
            const shippedBySub: Record<string, number> = {};
            const subMetaById: Record<string, {populationCount: number}> = {};
            for (const sf of subFeedTotals) {
              subMetaById[sf.sb.id as string] = {
                populationCount: Math.max(0, sf.started - sf.transferCount - sf.mortality),
              };
            }
            const tripsForForecast = [...trips].sort(
              (a, b) =>
                (a.date || '').localeCompare(b.date || '') ||
                (parseInt(a.order as string) || 0) - (parseInt(b.order as string) || 0) ||
                String(a.id || '').localeCompare(String(b.id || '')),
            );

            for (const trip of tripsForForecast) {
              const tripDate = typeof trip.date === 'string' ? trip.date : null;
              if (!tripDate) continue;
              const tripAgeRange = libCalcAgeRange(
                g.cycleId,
                new Date(tripDate + 'T12:00:00'),
                breedingCycles,
                farrowingRecs,
              );
              const cycleAgeDaysAtTrip =
                tripAgeRange && tripAgeRange.minDays != null
                  ? {minDays: tripAgeRange.minDays, maxDays: tripAgeRange.maxDays}
                  : null;
              const distribution = buildFarrowingAgeDistribution({
                cycleId: g.cycleId,
                asOfDate: tripDate,
                breedingCycles,
                farrowingRecs,
                cycleAgeDaysAtRef: cycleAgeDaysAtTrip,
              });
              const attributions = (Array.isArray(trip.subAttributions) ? trip.subAttributions : [])
                .map((a) => ({subId: a && a.subId, count: parseInt(a && (a.count as string)) || 0}))
                .filter((a) => a.subId && a.count > 0);

              if (attributions.length === 0) {
                if (cycleAgeDaysAtTrip) {
                  const min = (cycleAgeDaysAtTrip.minDays as number) * effectiveAdgLbsPerDay;
                  const max = (cycleAgeDaysAtTrip.maxDays as number) * effectiveAdgLbsPerDay;
                  out[trip.id as string] = {
                    min: Math.min(min, max),
                    max: Math.max(min, max),
                    avg: (min + max) / 2,
                    source: 'estimated-cycle-band',
                    hasActual: !!(tripAgeRange && tripAgeRange.hasActual),
                  };
                }
                continue;
              }

              let min: number | null = null;
              let max: number | null = null;
              let avgNumerator = 0;
              let countTotal = 0;
              let hasActual = false;
              let source: string | null = null;
              for (const a of attributions) {
                const meta = subMetaById[a.subId as string];
                const offset = shippedBySub[a.subId as string] || 0;
                shippedBySub[a.subId as string] = offset + a.count;
                if (!meta || !distribution) continue;
                const window = projectFarrowingAgeWindow(distribution, {
                  populationCount: meta.populationCount,
                  rankOffset: offset,
                  windowCount: a.count,
                });
                if (!window) continue;
                const wMin = Math.min(window.minDays, window.maxDays) * effectiveAdgLbsPerDay;
                const wMax = Math.max(window.minDays, window.maxDays) * effectiveAdgLbsPerDay;
                min = min == null ? wMin : Math.min(min, wMin);
                max = max == null ? wMax : Math.max(max, wMax);
                avgNumerator += window.avgDays * effectiveAdgLbsPerDay * a.count;
                countTotal += a.count;
                hasActual = hasActual || !!window.hasActual;
                source = source || window.source;
              }
              if (countTotal > 0 && min != null && max != null) {
                out[trip.id as string] = {min, max, avg: avgNumerator / countTotal, source, hasActual};
              }
            }
            return out;
          })();

          return (
            <div key={g.id} className="pig-batch-record__stack">
              <header className="pig-batch-record__header">
                <div className="pig-batch-record__title-row">
                  <RecordTitle>{group.batchName}</RecordTitle>
                  <Badge status={statusLabel(g, batchStatusMetrics)}>{statusLabel(g, batchStatusMetrics)}</Badge>
                  <div className="pig-batch-record__actions">
                    {g.status === 'active' ? (
                      <Button className="pig-batch-button" onClick={() => archiveBatch(g.id)}>
                        Mark Complete
                      </Button>
                    ) : (
                      <Button className="pig-batch-button" onClick={() => unarchiveBatch(g.id)}>
                        <BatchActionIcon kind="reopen" /> Reopen To In Process
                      </Button>
                    )}
                    <Button variant="primary" className="pig-batch-button" onClick={() => openMortalityModal(g.id)}>
                      <BatchActionIcon kind="add" /> Mortality
                    </Button>
                    {/* R25: the header's action while the Batch Details card
                        is open. Done is closeFeederForm: the draft flush, then
                        the partition flush, closing only once both confirm. */}
                    {feederEditor.editingId === g.id && (
                      <Button
                        variant="primary"
                        className="pig-batch-button"
                        disabled={feederEditor.busy}
                        onClick={() => void feederEditor.close()}
                        data-pig-batch-done={g.id}
                      >
                        Done
                      </Button>
                    )}
                  </div>
                </div>
                <div className="pig-batch-record__meta">
                  {!g.farmBorn && (
                    <span>
                      <i style={programDotStyle('pig')} /> Gilts <b>{g.giltCount}</b>
                    </span>
                  )}
                  {!g.farmBorn && (
                    <span>
                      Boars <b>{g.boarCount}</b>
                    </span>
                  )}
                  {currentPigCount !== null ? (
                    <span>
                      Current <b>{currentPigCount}</b>
                    </span>
                  ) : (
                    originalPigCount > 0 && (
                      <span>
                        Started <b>{originalPigCount}</b>
                      </span>
                    )
                  )}
                  <span>
                    Age <b>{ageRange.text}</b>
                    {!ageRange.hasActual && ageRange.text !== '—' && <small>(estimated)</small>}
                  </span>
                  {g.startDate && (
                    <span>
                      Started <b>{fmt(g.startDate)}</b>
                    </span>
                  )}
                  <span>
                    Daily reports{' '}
                    <b>
                      {hasSubBatches
                        ? subFeedTotals.reduce((sum, sf) => sum + sf.dailys.length, 0)
                        : batchDailys.length}
                    </b>
                  </span>
                </div>
              </header>

              {/* Summary stats */}
              {(() => {
                const anyDailys = hasSubBatches
                  ? subFeedTotals.some((sf) => sf.dailys.length > 0)
                  : batchDailys.length > 0;
                return trips.length > 0 || totalFeed > 0 || anyDailys;
              })() && (
                <div className="pig-batch-panel">
                  <div className="pig-batch-kpis">
                    {[
                      {
                        label: 'Total Feed',
                        val: totalFeed > 0 ? `${Math.round(totalFeed).toLocaleString()} lbs` : '—',
                        color: 'var(--text-primary)',
                        hint:
                          feedAllocatedOut > 0
                            ? `raw ${Math.round(rawFeed).toLocaleString()} − ${Math.round(feedAllocatedOut).toLocaleString()} transferred out`
                            : dailyFeedTotal > 0 && legacyFeed > 0
                              ? `${Math.round(dailyFeedTotal).toLocaleString()} from dailys + ${Math.round(legacyFeed).toLocaleString()} legacy`
                              : dailyFeedTotal > 0
                                ? `from ${batchDailys.length} daily reports`
                                : null,
                      },
                      {
                        label: 'Feed Per Pig',
                        val: (() => {
                          const transferredCount = hasSubBatches
                            ? subFeedTotals.reduce((s, sf) => s + sf.transferCount, 0)
                            : parentTransferAgg.count;
                          const mortalityCount = hasSubBatches
                            ? subFeedTotals.reduce((s, sf) => s + sf.mortality, 0)
                            : pigMortalityForBatch(g);
                          const finishers = Math.max(0, originalPigCount - transferredCount - mortalityCount);
                          return totalFeed > 0 && finishers > 0 ? `${Math.round(totalFeed / finishers)} lbs/pig` : '—';
                        })(),
                        color: 'var(--text-primary)',
                        hint: (() => {
                          const transferredCount = hasSubBatches
                            ? subFeedTotals.reduce((s, sf) => s + sf.transferCount, 0)
                            : parentTransferAgg.count;
                          const mortalityCount = hasSubBatches
                            ? subFeedTotals.reduce((s, sf) => s + sf.mortality, 0)
                            : pigMortalityForBatch(g);
                          const finishers = Math.max(0, originalPigCount - transferredCount - mortalityCount);
                          if (finishers <= 0) return null;
                          const parts = [`adjusted feed ÷ ${finishers}`];
                          if (transferredCount > 0 || mortalityCount > 0) {
                            const subParts = [`${originalPigCount} started`];
                            if (transferredCount > 0) subParts.push(`− ${transferredCount} transferred`);
                            if (mortalityCount > 0) subParts.push(`− ${mortalityCount} mortality`);
                            parts.push(`(${subParts.join(' ')})`);
                          }
                          return parts.join(' ');
                        })(),
                      },
                      {
                        label: 'Feed Cost',
                        val: totalFeedCost
                          ? `$${totalFeedCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                          : '—',
                        color: 'var(--text-primary)',
                        hint: perLbCost > 0 ? `$${perLbCost}/lb` : null,
                      },
                      {
                        label: 'Feed Conversion',
                        val: feedConversion ? `${feedConversion} lbs/lb` : '—',
                        color: 'var(--text-primary)',
                      },
                      {
                        label: 'Pigs Processed',
                        val: pigsProcessed,
                        color: 'var(--ink)',
                      },
                      {
                        label: 'Average Yield',
                        val: overallYield ? `${overallYield}%` : '—',
                        color: 'var(--text-primary)',
                      },
                    ].map((s) => (
                      <div key={s.label} className="pig-batch-kpi">
                        <div className="pig-batch-kpi__label">{s.label}</div>
                        <div className="pig-batch-kpi__value">{s.val === 0 ? '—' : s.val}</div>
                        {s.hint && (
                          <div className="pig-batch-kpi__hint" title={s.hint}>
                            {s.hint}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* R25: Batch Details, read first. Edit opens the batch's editor in
                  place (the same field block the modal rendered); only this card
                  renders its inputs. Gilts, boars, start date and age already
                  read on the meta line above, so the rows carry the rest (R12). */}
              <section className="pig-batch-panel" data-pig-batch-card="details">
                <div className="pig-batch-card-head">
                  <h2 className="pig-batch-card-title">Batch Details</h2>
                  {feederEditor.editingId !== g.id && (
                    <button
                      type="button"
                      className="wcf-clickable pig-batch-card-edit"
                      disabled={feederEditor.busy}
                      data-pig-batch-edit-card="details"
                      onClick={() => openBatchEditor(g)}
                    >
                      Edit
                    </button>
                  )}
                </div>
                {feederEditor.editingId === g.id ? (
                  <div data-pig-batch-editor="details">
                    {feederEditor.conflictNotice}
                    {feederEditor.renderFields()}
                  </div>
                ) : (
                  <dl className="pig-batch-detail-rows">
                    {[
                      ['Linked Cycle', cycle ? cycleLabel(cycle, cycleSeqMap) : '—'],
                      ['Original Pig Count', originalPigCount > 0 ? originalPigCount.toLocaleString() : '—'],
                      [
                        'Feed Credited To Transfers',
                        Number(g.feedAllocatedToTransfers) > 0
                          ? Math.round(Number(g.feedAllocatedToTransfers)).toLocaleString() + ' lbs'
                          : '—',
                      ],
                      [
                        'Feed Cost Rate',
                        g.perLbFeedCost != null && g.perLbFeedCost !== ''
                          ? '$' + parseFloat(String(g.perLbFeedCost)).toFixed(3) + '/lb'
                          : '—',
                      ],
                      ['Notes', g.notes || '—'],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </section>

              {/* Transferred-to-breeding note (per sub-batch breakdown) */}
              {(() => {
                const transferred = (breeders || []).filter(
                  (b) => b && b.transferredFromBatch && b.transferredFromBatch.batchName === g.batchName,
                );
                if (transferred.length === 0) return null;
                const bySub: Record<string, number> = {};
                transferred.forEach((b) => {
                  const k = (b.transferredFromBatch as PigTransferSource).subBatchName || g.batchName;
                  bySub[k as string] = (bySub[k as string] || 0) + 1;
                });
                return (
                  <div
                    style={{
                      padding: '10px 16px',
                      borderBottom: '1px solid var(--border)',
                      background: 'var(--bg-card)',
                      fontSize: 12,
                      color: 'var(--text-primary)',
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{fontWeight: 700}}>{'→ Breeding:'}</span>
                    {Object.entries(bySub).map(([sub, count], i) => (
                      <span key={sub}>
                        {(i > 0 ? ' · ' : '') +
                          count +
                          ' ' +
                          (count === 1 ? 'pig' : 'pigs') +
                          ' out of ' +
                          sub +
                          ' sent to breeding pigs group'}
                      </span>
                    ))}
                  </div>
                );
              })()}

              {/* Sub-Batches panel */}
              <section className="pig-batch-panel">
                <div className="pig-batch-panel__heading">
                  <h2>
                    Sub-Batches <span className="pig-batch-count">{subBatches.length}</span>
                  </h2>
                  <div style={{display: 'flex', gap: 6}}>
                    {showSubForm === g.id && !editSubId ? (
                      <Button className="pig-batch-button" onClick={() => closeSubForm(g.id)}>
                        Close
                      </Button>
                    ) : (
                      <Button
                        className="pig-batch-button"
                        onClick={() => {
                          setNotice(null);
                          setShowSubForm(g.id);
                          setEditSubId(null);
                          setSubForm({name: '', sex: 'Gilts', count: 0, notes: ''});
                        }}
                      >
                        <BatchActionIcon kind="add" /> Add Sub-Batch
                      </Button>
                    )}
                  </div>
                </div>

                {/* Sub-batch form (modal) */}
                {showSubForm === g.id && (
                  <Modal
                    title={editSubId ? 'Sub-Batch' : 'New Sub-Batch'}
                    size="sm"
                    autosave
                    onClose={() => closeSubForm(g.id)}
                    footer={<ModalFooter onDelete={editSubId ? () => deleteSubBatch(g.id, editSubId) : undefined} />}
                  >
                    <div style={{padding: '16px 20px'}}>
                      <div style={{marginBottom: 10}}>
                        <InlineNotice notice={notice as Notice | null} onDismiss={() => setNotice(null)} />
                        <PigConflictNotice conflict={subConflict} />
                      </div>
                      <div className={recordFieldRowClass}>
                        <span style={recordFieldLabel}>Sub-batch name *</span>
                        <div>
                          <input
                            value={subForm.name}
                            onChange={(e) => updSub(g.id, 'name', e.target.value)}
                            placeholder="e.g. P-26-01 A (GILTS)"
                            style={recordControl}
                          />
                          <div style={{fontSize: 10, color: 'var(--ink-faint)', marginTop: 2}}>
                            Must match the label used on daily reports
                          </div>
                        </div>
                      </div>
                      {!editSubId ? (
                        <>
                          <div className={recordFieldRowClass}>
                            <span style={recordFieldLabel}>Sex *</span>
                            <select
                              value={subForm.sex || 'Gilts'}
                              onChange={(e) => updSub(g.id, 'sex', e.target.value)}
                              style={recordControl}
                            >
                              <option value="Gilts">Gilts</option>
                              <option value="Boars">Boars</option>
                            </select>
                          </div>
                          <div className={recordFieldRowClass}>
                            <span style={recordFieldLabel}>Count *</span>
                            <input
                              type="number"
                              min="0"
                              value={subForm.count || ''}
                              onChange={(e) => updSub(g.id, 'count', e.target.value)}
                              style={recordControl}
                              placeholder={(() => {
                                const sex = subForm.sex || 'Gilts';
                                const parentTotal =
                                  sex === 'Boars'
                                    ? parseInt(g.boarCount as string) || 0
                                    : parseInt(g.giltCount as string) || 0;
                                const usedBy = (g.subBatches || [])
                                  .filter(
                                    (s) =>
                                      (sex === 'Boars'
                                        ? parseInt(s.boarCount as string) || 0
                                        : parseInt(s.giltCount as string) || 0) > 0,
                                  )
                                  .reduce(
                                    (a, s) =>
                                      a +
                                      (sex === 'Boars'
                                        ? parseInt(s.boarCount as string) || 0
                                        : parseInt(s.giltCount as string) || 0),
                                    0,
                                  );
                                const remaining = Math.max(0, parentTotal - usedBy);
                                return remaining > 0 ? `Up to ${remaining} available` : '0 available';
                              })()}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              gridColumn: '1/-1',
                              padding: '10px 12px',
                              background: 'var(--surface-2)',
                              border: '1px solid var(--border)',
                              borderRadius: 10,
                            }}
                          >
                            <div style={{fontSize: 11, color: 'var(--ink-faint)', marginBottom: 4}}>
                              Sex + count are locked. Edit the parent batch to redistribute.
                            </div>
                            <div style={{display: 'flex', gap: 14, fontSize: 13, color: 'var(--ink)', fontWeight: 600}}>
                              {(parseInt(subForm.giltCount as unknown as string) || 0) > 0 && (
                                <span>Gilts: {subForm.giltCount}</span>
                              )}
                              {(parseInt(subForm.boarCount as unknown as string) || 0) > 0 && (
                                <span>Boars: {subForm.boarCount}</span>
                              )}
                              <span style={{color: 'var(--ink-muted)'}}>
                                Original count:{' '}
                                {(parseInt(subForm.giltCount as unknown as string) || 0) +
                                  (parseInt(subForm.boarCount as unknown as string) || 0)}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                      <div className={recordFieldRowClass}>
                        <span style={recordFieldLabel}>Notes</span>
                        <input
                          value={subForm.notes}
                          onChange={(e) => updSub(g.id, 'notes', e.target.value)}
                          placeholder="Add notes"
                          style={recordControl}
                        />
                      </div>
                    </div>
                  </Modal>
                )}

                {subBatches.length === 0 && showSubForm !== g.id && (
                  <div style={{fontSize: 11, color: 'var(--ink-faint)', padding: '2px 0 6px'}}>
                    No sub-batches yet — daily reports start once you add a sub-batch. Add Sub-Batches to split A/B
                    groups.
                  </div>
                )}

                <div className="pig-batch-subgrid">
                  {subBatches.map((sb, subIndex) => {
                    const sft = subFeedTotals.find((x) => x.sb.id === sb.id) || {
                      adjustedFeed: 0,
                      rawFeed: 0,
                      transferFeedCredit: 0,
                      dailys: [],
                      currentCount: null,
                      started: 0,
                      transferCount: 0,
                      tripPigs: 0,
                      mortality: 0,
                    };
                    // Planned-trip projection for this sub (commit 4a).
                    // Ronnie rule: forecast weight stays DOB/farrowing age at
                    // trip date × Global ADG. Weigh-in weights do not alter
                    // planned-trip forecast weights or dates.
                    const subGiltCount = parseInt(sb.giltCount as string) || 0;
                    const subBoarCount = parseInt(sb.boarCount as string) || 0;
                    const isMixedSex = subGiltCount > 0 && subBoarCount > 0;
                    const todayStr = todayISO();
                    const cycleAgeForRender = g.cycleId
                      ? libCalcAgeRange(g.cycleId, new Date(todayStr + 'T12:00:00'), breedingCycles, farrowingRecs)
                      : null;
                    const cycleAgeDaysAtRef =
                      cycleAgeForRender && cycleAgeForRender.minDays != null
                        ? {minDays: cycleAgeForRender.minDays, maxDays: cycleAgeForRender.maxDays}
                        : null;
                    const ageDistributionAtRef = buildFarrowingAgeDistribution({
                      cycleId: g.cycleId,
                      asOfDate: todayStr,
                      breedingCycles,
                      farrowingRecs,
                      cycleAgeDaysAtRef,
                    });
                    const forecastPopulationCount = Math.max(0, sft.started - sft.transferCount - sft.mortality);
                    const plannedRawForSub = ((g.plannedProcessingTrips || []) as PigBatchPlannedTrip[]).filter(
                      (t) => t.subBatchId === sb.id,
                    );
                    const plannedProjected = recalculateProjections(plannedRawForSub, {
                      referenceDate: todayStr,
                      globalAdgLbsPerDay: effectiveAdgLbsPerDay,
                      cycleAgeDaysAtRef,
                      ageDistributionAtRef,
                      populationCount: forecastPopulationCount,
                      alreadyShippedCount: sft.tripPigs,
                      targetWeightLbs: PLANNED_TRIP_TARGET_WEIGHT_LBS,
                      minSize: PLANNED_TRIP_MIN_SIZE,
                      overWeightWarnLbs: PLANNED_TRIP_OVER_WEIGHT_WARN_LBS,
                    });
                    return (
                      <div key={sb.id} className="pig-batch-sub">
                        <Collapsible
                          stateKey={'pig-sub-batch:' + g.id + ':' + sb.id}
                          defaultOpen={sb.status !== 'processed'}
                          count={sft.currentCount ?? sft.started}
                          label={
                            <span className="pig-batch-group-label">
                              <span
                                aria-hidden="true"
                                className="pig-batch-group-dot"
                                style={{
                                  background:
                                    sb.status === 'processed'
                                      ? 'var(--group-archived)'
                                      : `var(--group-${(subIndex % 6) + 1})`,
                                }}
                              />
                              <strong>{sb.name}</strong>
                            </span>
                          }
                          summary={<Badge status={statusLabel(sb, sft)}>{statusLabel(sb, sft)}</Badge>}
                        >
                          <div className="pig-batch-sub__body">
                            <p className="pig-batch-sub__sex">
                              {isMixedSex ? 'Mixed' : subGiltCount > 0 ? 'Gilts' : subBoarCount > 0 ? 'Boars' : 'Pigs'}
                            </p>
                            <div className="pig-batch-sub__stats">
                              <div>
                                <span>Started</span>
                                <b>{sft.started}</b>
                              </div>
                              <div>
                                <span>Feed</span>
                                <b>
                                  {sft.adjustedFeed > 0 ? `${Math.round(sft.adjustedFeed).toLocaleString()} lbs` : '—'}
                                </b>
                              </div>
                              <div
                                title={`adjusted feed ÷ ${Math.max(0, sft.started - sft.transferCount - sft.mortality)} (started ${sft.started}${sft.transferCount ? ` − ${sft.transferCount} transferred` : ''}${sft.mortality ? ` − ${sft.mortality} mortality` : ''})`}
                              >
                                <span>Per pig</span>
                                <b>
                                  {sft.adjustedFeed > 0 && sft.started - sft.transferCount - sft.mortality > 0
                                    ? `${Math.round(sft.adjustedFeed / (sft.started - sft.transferCount - sft.mortality)).toLocaleString()} lbs`
                                    : '—'}
                                </b>
                              </div>
                              <div>
                                <span>Reports</span>
                                <b>{sft.dailys.length}</b>
                              </div>
                            </div>
                            <div className="pig-batch-sub__current">
                              <PlannerIcon iconKey={ANIMAL_ICON_KEYS.pig} size={14} /> {sft.currentCount} current
                              {sft.transferFeedCredit > 0 && (
                                <span
                                  title={`raw ${Math.round(sft.rawFeed).toLocaleString()} − ${Math.round(sft.transferFeedCredit).toLocaleString()} credited to ${sft.transferCount} transferred`}
                                >
                                  (−{Math.round(sft.transferFeedCredit).toLocaleString()} lbs → breeding)
                                </span>
                              )}
                            </div>
                            <div className="pig-batch-sub__actions">
                              {sb.status === 'active' ? (
                                <Button
                                  className="pig-batch-button"
                                  onClick={() => archiveSubBatch(g.id, sb.id as string)}
                                >
                                  Mark Complete
                                </Button>
                              ) : (
                                <Button
                                  className="pig-batch-button"
                                  onClick={() => unarchiveSubBatch(g.id, sb.id as string)}
                                >
                                  <BatchActionIcon kind="reopen" /> Reopen To In Process
                                </Button>
                              )}
                              <Button
                                className="pig-batch-button"
                                onClick={() => {
                                  clearTimeout(subAutoSaveTimer.current ?? undefined);
                                  setNotice(null);
                                  setShowSubForm(g.id);
                                  setEditSubId(sb.id);
                                  setSubForm({
                                    name: sb.name as string,
                                    giltCount: (sb.giltCount || 0) as number,
                                    boarCount: (sb.boarCount || 0) as number,
                                    originalPigCount: (sb.originalPigCount || 0) as number,
                                    notes: sb.notes || '',
                                  });
                                }}
                              >
                                <BatchActionIcon kind="edit" /> Rename
                              </Button>
                            </div>
                          </div>
                          {/* Planned trips for this sub (commit 4a, read-only).
                        Compact band beneath the sub row. Cards show date,
                        count, projected weight range, ready badge, and
                        warnings. Edit controls land in commit 4b. */}
                          {sb.status !== 'processed' && (
                            <div
                              data-planned-trips-sub={sb.id}
                              style={{
                                margin: '0 0 8px 0',
                                padding: '6px 10px',
                                borderRadius: 10,
                                border: '1px solid var(--border)',
                                background: 'var(--surface)',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 10,
                                  color: 'var(--ink-muted)',
                                  textTransform: 'uppercase',
                                  fontWeight: 600,
                                  marginBottom: 6,
                                }}
                              >
                                Planned Trips (Forecast)
                              </div>
                              {isMixedSex && (
                                <div style={{fontSize: 11, color: 'var(--warn-ink)', fontStyle: 'italic'}}>
                                  Mixed sex sub: split into separate gilts/boars subgroups before planning trips.
                                </div>
                              )}
                              {!isMixedSex && !g.cycleId && (
                                <div style={{fontSize: 11, color: 'var(--ink-muted)', fontStyle: 'italic'}}>
                                  Link a breeding cycle to see planned trips.
                                </div>
                              )}
                              {!isMixedSex && g.cycleId && effectiveAdgLbsPerDay == null && (
                                <div style={{fontSize: 11, color: 'var(--ink-muted)', fontStyle: 'italic'}}>
                                  Set Global ADG above to see planned trips.
                                </div>
                              )}
                              {!isMixedSex &&
                                g.cycleId &&
                                effectiveAdgLbsPerDay != null &&
                                plannedProjected.length === 0 && (
                                  <div style={{fontSize: 11, color: 'var(--ink-muted)', fontStyle: 'italic'}}>
                                    Projection unavailable — cycle age range not yet usable.
                                  </div>
                                )}
                              {/* Manual + Add planned trip (admin/management only).
                              Sex comes from the sub itself (non-mixed subs are
                              gilt-only or boar-only). Mixed-sex hint above already
                              tells the user to split before planning. Date is
                              free-form per Codex's pre-build answer #2 — the chain
                              re-sorts by date+order on render. */}
                              {!isMixedSex && isManager && (
                                <div data-planned-trip-add-shell={sb.id} style={{marginBottom: 6}}>
                                  {addingTripFor && addingTripFor.subBatchId === sb.id ? (
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 6,
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        background: 'var(--surface)',
                                        border: '1px solid var(--border-strong)',
                                        borderRadius: 10,
                                        padding: '6px 8px',
                                        fontSize: 11,
                                      }}
                                    >
                                      <input
                                        data-planned-trip-add-date={sb.id}
                                        type="date"
                                        value={addingTripDate}
                                        onChange={(e) => setAddingTripDate(e.target.value)}
                                        style={{
                                          fontSize: 11,
                                          padding: '2px 4px',
                                          border: '1px solid var(--border-strong)',
                                          borderRadius: 10,
                                          fontFamily: 'inherit',
                                          width: 132,
                                          minWidth: 132,
                                          maxWidth: 132,
                                          flex: '0 0 132px',
                                          boxSizing: 'border-box',
                                        }}
                                      />
                                      <input
                                        data-planned-trip-add-count={sb.id}
                                        type="number"
                                        min={1}
                                        placeholder="pigs"
                                        value={addingTripCount}
                                        onChange={(e) => setAddingTripCount(e.target.value)}
                                        style={{
                                          fontSize: 11,
                                          padding: '2px 4px',
                                          border: '1px solid var(--border-strong)',
                                          borderRadius: 10,
                                          fontFamily: 'inherit',
                                          width: 70,
                                        }}
                                      />
                                      <span style={{color: 'var(--ink-muted)'}}>{addingTripFor.sex}</span>
                                      <Button
                                        data-planned-trip-add-save={sb.id}
                                        onClick={() => {
                                          const r = addPlannedTripById(g.id, {
                                            subBatchId: sb.id,
                                            sex: addingTripFor.sex,
                                            date: addingTripDate,
                                            plannedCount: parseInt(addingTripCount),
                                          });
                                          if (r && r.error) {
                                            setAddingTripError(r.error);
                                            return;
                                          }
                                          setAddingTripFor(null);
                                          setAddingTripDate('');
                                          setAddingTripCount('');
                                          setAddingTripError('');
                                        }}
                                      >
                                        Add
                                      </Button>
                                      <Button
                                        onClick={() => {
                                          setAddingTripFor(null);
                                          setAddingTripDate('');
                                          setAddingTripCount('');
                                          setAddingTripError('');
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                      {addingTripError && (
                                        <span
                                          data-planned-trip-add-error={sb.id}
                                          style={{color: 'var(--danger-ink)', fontSize: 10, fontStyle: 'italic'}}
                                        >
                                          {addingTripError}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    (() => {
                                      // Codex pig planned trips lane: when ANY trip in a
                                      // (subBatchId, sex) chain is locked, disable Add for
                                      // that chain — an add would draw pigs from the locked
                                      // chain's plannedCount reservoir.
                                      const giltChainLocked = isChainLocked(
                                        g.plannedProcessingTrips || [],
                                        sb.id,
                                        'gilt',
                                      );
                                      const boarChainLocked = isChainLocked(
                                        g.plannedProcessingTrips || [],
                                        sb.id,
                                        'boar',
                                      );
                                      return (
                                        <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                                          {(parseInt(sb.giltCount as string) || 0) > 0 && (
                                            <Button
                                              data-planned-trip-add-button={`${sb.id}-gilt`}
                                              data-planned-trip-add-locked={giltChainLocked ? 'true' : 'false'}
                                              disabled={giltChainLocked}
                                              onClick={() => {
                                                if (giltChainLocked) return;
                                                setAddingTripFor({groupId: g.id, subBatchId: sb.id, sex: 'gilt'});
                                                setAddingTripDate(new Date().toISOString().slice(0, 10));
                                                setAddingTripCount('');
                                                setAddingTripError('');
                                              }}
                                              title={
                                                giltChainLocked
                                                  ? 'Gilt chain has a locked trip — unlock first'
                                                  : undefined
                                              }
                                            >
                                              + Add Gilt Trip
                                            </Button>
                                          )}
                                          {(parseInt(sb.boarCount as string) || 0) > 0 && (
                                            <Button
                                              data-planned-trip-add-button={`${sb.id}-boar`}
                                              data-planned-trip-add-locked={boarChainLocked ? 'true' : 'false'}
                                              disabled={boarChainLocked}
                                              onClick={() => {
                                                if (boarChainLocked) return;
                                                setAddingTripFor({groupId: g.id, subBatchId: sb.id, sex: 'boar'});
                                                setAddingTripDate(new Date().toISOString().slice(0, 10));
                                                setAddingTripCount('');
                                                setAddingTripError('');
                                              }}
                                              title={
                                                boarChainLocked
                                                  ? 'Boar chain has a locked trip — unlock first'
                                                  : undefined
                                              }
                                            >
                                              + Add Boar Trip
                                            </Button>
                                          )}
                                        </div>
                                      );
                                    })()
                                  )}
                                </div>
                              )}
                              {!isMixedSex &&
                                g.cycleId &&
                                effectiveAdgLbsPerDay != null &&
                                plannedProjected.length > 0 && (
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                                      gap: 6,
                                    }}
                                  >
                                    {plannedProjected.map((t, ti) => {
                                      const projRange =
                                        t.projectedMinLbs != null && t.projectedMaxLbs != null
                                          ? `${Math.round(t.projectedMinLbs)} – ${Math.round(t.projectedMaxLbs)} lb${
                                              t.projectionHasActualFarrowing ? '' : ' (est.)'
                                            }`
                                          : '—';
                                      const projAvg =
                                        t.projectedAvgLbs != null ? `~${Math.round(t.projectedAvgLbs)} lb avg` : '';
                                      // Move targets within this (sub, sex) chain. plannedProjected
                                      // is already sorted by (date, order). nextSameSex receives
                                      // forward moves; prevSameSex receives back moves.
                                      const nextSameSex = plannedProjected.slice(ti + 1).find((nt) => nt.sex === t.sex);
                                      const prevSameSex = plannedProjected
                                        .slice(0, ti)
                                        .reverse()
                                        .find((pt) => pt.sex === t.sex);
                                      // Chain trip count for THIS sex governs the delete-disable.
                                      // A chain of one trip cannot be deleted (deletion would
                                      // lose the planned-count signal).
                                      const sameSexChainCount = plannedProjected.filter(
                                        (ct) => ct.sex === t.sex,
                                      ).length;
                                      const canDelete = isManager && sameSexChainCount > 1;
                                      const isEditingDate = editingPlannedTripId === t.id;
                                      // Lock state for this card. Locked trips hide every
                                      // mutation affordance (date, ±1d, move, delete) so
                                      // operators can only re-enable mutation by going
                                      // through the inline two-step unlock.
                                      const lockEntry = plannedTripLocks && plannedTripLocks[t.id as string];
                                      const tripLocked = !!(lockEntry && lockEntry.locked);
                                      const lockedByLabel = tripLocked
                                        ? 'Locked by user: ' + (lockEntry.lockedByName || 'Unknown')
                                        : null;
                                      return (
                                        <div
                                          key={t.id}
                                          data-planned-trip-id={t.id}
                                          data-pig-trip={t.id}
                                          data-planned-trip-sex={t.sex}
                                          data-planned-trip-locked={tripLocked ? 'true' : 'false'}
                                          style={{
                                            background: tripLocked ? 'var(--surface-2)' : 'var(--surface)',
                                            border: '1px solid var(--border)',
                                            borderRadius: 10,
                                            padding: '6px 10px',
                                            fontSize: 11,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              gap: 4,
                                              flexWrap: 'wrap',
                                            }}
                                          >
                                            <span className="pig-batch-planned-trip__heading">
                                              <span data-pig-trip-number={t.id}>
                                                {processingTripOrdinals[`${g.id}:${t.id}`] != null
                                                  ? `Trip ${processingTripOrdinals[`${g.id}:${t.id}`]}`
                                                  : 'Trip'}
                                              </span>
                                              <span>{fmt(t.date)}</span>
                                            </span>
                                            {tripLocked && (
                                              <span
                                                data-planned-trip-locked-by={t.id}
                                                style={{
                                                  fontSize: 10,
                                                  fontWeight: 700,
                                                  padding: '2px 8px',
                                                  borderRadius: 10,
                                                  background: 'var(--surface-2)',
                                                  color: 'var(--text-secondary)',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: 4,
                                                }}
                                                title={
                                                  lockEntry && lockEntry.lockedAt
                                                    ? 'Locked ' + fmt(lockEntry.lockedAt)
                                                    : undefined
                                                }
                                              >
                                                🔒 {lockedByLabel}
                                              </span>
                                            )}
                                            {!tripLocked && isManager && (
                                              <OverflowMenu
                                                label="Planned Trip Actions"
                                                items={[
                                                  {
                                                    key: 'edit-date',
                                                    label: 'Change Date',
                                                    onSelect: () => {
                                                      if (isEditingDate) {
                                                        setEditingPlannedTripId(null);
                                                        setEditingPlannedTripDate('');
                                                        return;
                                                      }
                                                      setEditingPlannedTripId(t.id as string);
                                                      setEditingPlannedTripDate(t.date || '');
                                                    },
                                                    attrs: {'data-planned-trip-edit-date': String(t.id)},
                                                  },
                                                  {
                                                    key: 'date-back',
                                                    label: 'Move Date 1 Day Earlier',
                                                    onSelect: () =>
                                                      shiftPlannedTripDateById(g.id, t.id as string, t.date, -1),
                                                    attrs: {'data-planned-trip-date-back': String(t.id)},
                                                  },
                                                  {
                                                    key: 'date-forward',
                                                    label: 'Move Date 1 Day Later',
                                                    onSelect: () =>
                                                      shiftPlannedTripDateById(g.id, t.id as string, t.date, 1),
                                                    attrs: {'data-planned-trip-date-forward': String(t.id)},
                                                  },
                                                  {
                                                    key: 'lock',
                                                    label: 'Lock',
                                                    onSelect: () => lockPlannedTrip(t.id),
                                                    attrs: {'data-planned-trip-lock': String(t.id)},
                                                  },
                                                  ...(prevSameSex
                                                    ? [
                                                        {
                                                          key: 'move-back',
                                                          label: 'Move 1 Pig To Earlier Trip',
                                                          onSelect: () =>
                                                            movePlannedTripPigsById(
                                                              g.id,
                                                              t.id as string,
                                                              prevSameSex.id as string,
                                                            ),
                                                          attrs: {'data-planned-trip-move-back': String(t.id)},
                                                          disabled:
                                                            (parseInt(t.plannedCount as unknown as string) || 0) <= 0,
                                                        },
                                                      ]
                                                    : []),
                                                  ...(nextSameSex
                                                    ? [
                                                        {
                                                          key: 'move-forward',
                                                          label: 'Move 1 Pig To Later Trip',
                                                          onSelect: () =>
                                                            movePlannedTripPigsById(
                                                              g.id,
                                                              t.id as string,
                                                              nextSameSex.id as string,
                                                            ),
                                                          attrs: {'data-planned-trip-move-forward': String(t.id)},
                                                          disabled:
                                                            (parseInt(t.plannedCount as unknown as string) || 0) <= 0,
                                                        },
                                                      ]
                                                    : []),
                                                  ...(canDelete
                                                    ? [
                                                        {
                                                          key: 'delete',
                                                          label: 'Delete',
                                                          onSelect: () => deletePlannedTripById(g.id, t.id as string),
                                                          attrs: {'data-planned-trip-delete': String(t.id)},
                                                          danger: true,
                                                        },
                                                      ]
                                                    : []),
                                                ]}
                                              />
                                            )}
                                            {!tripLocked && isEditingDate && (
                                              <input
                                                data-planned-trip-date-input={t.id}
                                                type="date"
                                                value={editingPlannedTripDate}
                                                onChange={(e) => {
                                                  const nextDate = e.target.value;
                                                  setEditingPlannedTripDate(nextDate);
                                                  if (nextDate && nextDate !== t.date) {
                                                    setPlannedTripDateById(g.id, t.id as string, nextDate);
                                                  }
                                                }}
                                                style={{
                                                  fontSize: 11,
                                                  padding: '2px 4px',
                                                  border: '1px solid var(--border-strong)',
                                                  borderRadius: 10,
                                                  fontFamily: 'inherit',
                                                  width: 132,
                                                }}
                                              />
                                            )}
                                            <span style={{color: 'var(--text-primary)', fontWeight: 600}}>
                                              {t.plannedCount} {t.sex === 'gilt' ? 'gilt' : 'boar'}
                                              {t.plannedCount === 1 ? '' : 's'}
                                            </span>
                                          </div>
                                          <div style={{color: 'var(--ink)'}}>{projRange}</div>
                                          {projAvg && <div style={{color: 'var(--ink-muted)'}}>{projAvg}</div>}
                                          <div style={{display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2}}>
                                            {t.warnings.includes('overweight') ? (
                                              <Badge variant="warn">Over {PLANNED_TRIP_OVER_WEIGHT_WARN_LBS} lb</Badge>
                                            ) : t.warnings.includes('undersized') ? (
                                              <Badge variant="warn">Under {PLANNED_TRIP_MIN_SIZE} Pigs</Badge>
                                            ) : t.ready ? (
                                              <Badge variant="ok">Ready</Badge>
                                            ) : null}
                                            {/* Quiet deep link to this trip's Processing record
                                          (source key pig:<groupId>:<tripId>). */}
                                            <Button
                                              type="button"
                                              data-pig-trip-processing-link={t.id}
                                              onClick={() =>
                                                navigateToProcessingRoute(
                                                  navigate,
                                                  processingSourceRoute('pig', g.id, t.id),
                                                )
                                              }
                                              style={{marginLeft: 'auto'}}
                                            >
                                              Processing →
                                            </Button>
                                          </div>
                                          {/* Move arrows + delete (admin/management).
                                          ← back: send 1 pig from this trip to the PREVIOUS
                                          chain trip (heaviest in current rank window).
                                          → forward: send 1 pig from this trip to the NEXT
                                          chain trip (lightest in current rank window).
                                          First trip: forward only. Last trip: back only.
                                          Middle trips: both. Disabled when this trip has
                                          0 pigs to give. */}

                                          {/* Locked trip — admin/management can unlock via a
                                          two-step inline confirmation. No window.confirm. */}
                                          {tripLocked &&
                                            isManager &&
                                            (unlockingTripId === t.id ? (
                                              <div
                                                data-planned-trip-unlock-warning={t.id}
                                                style={{
                                                  display: 'flex',
                                                  gap: 6,
                                                  flexWrap: 'wrap',
                                                  marginTop: 4,
                                                  padding: '6px 8px',
                                                  border: '1px solid var(--border)',
                                                  background: 'var(--danger-soft)',
                                                  borderRadius: 10,
                                                  alignItems: 'center',
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    color: 'var(--danger-ink)',
                                                    fontSize: 11,
                                                    lineHeight: 1.3,
                                                    flex: '1 1 100%',
                                                  }}
                                                >
                                                  This trip has already been scheduled with the processor. Only unlock
                                                  if you have rescheduled with the processor.
                                                </span>
                                                <Button
                                                  data-planned-trip-unlock-cancel={t.id}
                                                  onClick={() => setUnlockingTripId(null)}
                                                >
                                                  Cancel
                                                </Button>
                                                <Button
                                                  data-planned-trip-unlock-confirm={t.id}
                                                  onClick={() => unlockPlannedTrip(t.id)}
                                                >
                                                  Confirm unlock
                                                </Button>
                                              </div>
                                            ) : (
                                              <div style={{display: 'flex', marginTop: 4}}>
                                                <OverflowMenu
                                                  label="Locked Trip Actions"
                                                  items={[
                                                    {
                                                      key: 'unlock',
                                                      label: 'Unlock',
                                                      onSelect: () => setUnlockingTripId(t.id as string),
                                                      attrs: {'data-planned-trip-unlock': String(t.id)},
                                                    },
                                                  ]}
                                                />
                                              </div>
                                            ))}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                            </div>
                          )}
                        </Collapsible>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Processing Trips */}
              <section className="pig-batch-panel pig-batch-trips">
                <div className="pig-batch-panel__heading">
                  <h2>
                    Processing Trips <span className="pig-batch-count">{trips.length}</span>
                  </h2>
                  <span className="pig-batch-panel__description">
                    {totalLive > 0 ? `${Math.round(totalLive).toLocaleString()} lb total live weight · ` : ''}Trips
                    originate from weigh-ins via Send-to-Trip
                  </span>
                </div>

                {/* Trip form (modal) */}
                {showTripForm && (
                  <Modal title="Processing Trip" autosave onClose={closeTripForm} footer={<ModalFooter />}>
                    <InlineNotice notice={notice as Notice | null} />
                    {tripRefreshPending && <Button onClick={() => void retryTripRefresh()}>Retry Refresh</Button>}
                    <div style={{padding: '16px 20px'}}>
                      <div className={recordFieldRowClass}>
                        <span style={recordFieldLabel}>Processing date</span>
                        <input
                          type="date"
                          value={tripForm.date}
                          onChange={(e) => updTrip('date', e.target.value)}
                          style={recordControl}
                        />
                      </div>
                      <div className={recordFieldRowClass}>
                        <span style={recordFieldLabel}>Number of pigs</span>
                        <div data-pig-trip-source-count={editTripId || 'new'} style={recordControl}>
                          {(() => {
                            const source: PigTripSourceSummary = editTripId
                              ? (tripSourceSummary(editTripId) as PigTripSourceSummary)
                              : {weights: [], count: 0, totalLive: 0, avgLive: null, counts: {}};
                            const actual = processingTripWithResolvedWeights(tripForm, {sourceSummary: source});
                            const count =
                              parseInt(actual.pigCount as unknown as string) ||
                              parseLiveWeights(actual.liveWeights).length;
                            if (source.count > 0) return source.count;
                            return count > 0 ? count + ' (stored legacy)' : 'No linked weigh-in entries';
                          })()}
                        </div>
                      </div>
                      <div className={recordFieldRowClass}>
                        <span style={recordFieldLabel}>Live weights</span>
                        <div>
                          {(() => {
                            const source: PigTripSourceSummary = editTripId
                              ? (tripSourceSummary(editTripId) as PigTripSourceSummary)
                              : {weights: [], count: 0, totalLive: 0, avgLive: null, counts: {}};
                            const actual = processingTripWithResolvedWeights(tripForm, {sourceSummary: source});
                            const storedWeights = parseLiveWeights(actual.liveWeights);
                            const displayWeights = source.weights.length > 0 ? source.weights : storedWeights;
                            const totalLive = displayWeights.reduce((a, b) => a + b, 0);
                            const avgLive = displayWeights.length > 0 ? totalLive / displayWeights.length : null;
                            const keys = Object.keys(source.counts || {});
                            return (
                              <>
                                <div data-pig-trip-source-weights={editTripId || 'new'} style={recordControl}>
                                  {displayWeights.length > 0 ? displayWeights.join(', ') : 'No source weights'}
                                </div>
                                {displayWeights.length > 0 && (
                                  <div style={{fontSize: 11, color: 'var(--text-secondary)', marginTop: 3}}>
                                    {displayWeights.length} pigs {'\u00b7'} Total: {Math.round(totalLive)} lbs
                                    {'\u00b7'} Avg: {Math.round(avgLive as number)} lbs/pig
                                    {source.count === 0 ? ' (stored legacy)' : ''}
                                  </div>
                                )}
                                {keys.length > 0 && (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: 'var(--text-primary)',
                                      marginTop: 6,
                                      padding: '5px 9px',
                                      background: 'var(--bg-card)',
                                      border: '1px solid var(--border)',
                                      borderRadius: 10,
                                    }}
                                  >
                                    <strong>Sources:</strong>{' '}
                                    {keys.map((k) => k + ' (' + source.counts[k] + ')').join(', ')}
                                  </div>
                                )}
                                {editTripId && source.count === 0 && (
                                  <div
                                    data-pig-trip-source-fallback={editTripId}
                                    style={{
                                      fontSize: 11,
                                      color: 'var(--warn-ink)',
                                      marginTop: 6,
                                      padding: '5px 9px',
                                      background: 'var(--warn-soft)',
                                      border: '1px solid var(--border)',
                                      borderRadius: 10,
                                    }}
                                  >
                                    No linked weigh-in entries found. This existing trip is using stored legacy weights;
                                    new processing trips still come only from Send-to-Trip weigh-in rows.
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div className={recordFieldRowClass}>
                        <span style={recordFieldLabel}>Hanging weight (lbs) — total for this trip</span>
                        <div>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={tripForm.hangingWeight || ''}
                            onChange={(e) => updTrip('hangingWeight', e.target.value)}
                            style={recordControl}
                          />
                          {(() => {
                            const source = editTripId ? tripSourceSummary(editTripId as string) : null;
                            const sourceTrip = processingTripWithResolvedWeights(tripForm, {sourceSummary: source});
                            // This is the FEEDER-BATCH yield: the field above
                            // is the batch's hanging weight, and the
                            // denominator is the trip's own liveWeights. An
                            // attached breeder's carcass is recorded on her
                            // attachment, not here, so this percentage is
                            // complete either way — it is only LABELLED so it
                            // cannot be read as the whole trip's yield.
                            const editTrip = (trips.find((x) => x.id === editTripId) || null) as PigBatchTrip | null;
                            const editHasBreeders = resolveTripBreeders(editTrip, breeders).length > 0;
                            return tripForm.hangingWeight > 0 && tripTotalLive(sourceTrip) > 0 ? (
                              <div style={{fontSize: 11, color: 'var(--text-secondary)', marginTop: 3}}>
                                {editHasBreeders ? 'Batch carcass yield' : 'Carcass yield'}: {tripYield(sourceTrip)}%
                                {editHasBreeders ? ' (excludes added breeding pigs)' : ''}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      <div className={recordFieldRowClass}>
                        <span style={recordFieldLabel}>Notes</span>
                        <input
                          value={tripForm.notes}
                          onChange={(e) => updTrip('notes', e.target.value)}
                          placeholder="Any notes for this trip..."
                          style={recordControl}
                        />
                      </div>
                    </div>
                    {editTripId && (
                      <div
                        style={{
                          padding: '12px 20px',
                          borderTop: '1px solid var(--border)',
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          variant="danger"
                          onClick={() => {
                            deleteTrip(g.id, editTripId as string);
                          }}
                        >
                          Delete
                        </Button>
                        {/* Quiet deep link to this trip's Processing record.
                              Close the modal first so it isn't left floating
                              over the Processing view. */}
                        <Button
                          type="button"
                          data-pig-trip-processing-link-modal={editTripId}
                          onClick={async () => {
                            const tripId: string | null = editTripId;
                            if (!(await closeTripForm())) return;
                            navigateToProcessingRoute(navigate, processingSourceRoute('pig', g.id, tripId));
                          }}
                          style={{marginLeft: 'auto'}}
                        >
                          Processing →
                        </Button>
                      </div>
                    )}
                  </Modal>
                )}

                {/* Trip list */}
                {trips.length === 0 && !showTripForm && (
                  <div style={{color: 'var(--ink-faint)', fontSize: 11, padding: '4px 0 8px'}}>
                    No processing trips yet — they’ll appear here once you Send-to-Trip from /pig/weighins
                  </div>
                )}
                <div className="pig-batch-trips__list">
                  {trips.map((t) => {
                    const source = tripSourceSummary(t.id) as PigTripSourceSummary;
                    const sourceTrip = processingTripWithResolvedWeights(t, {sourceSummary: source});
                    const live = tripTotalLive(sourceTrip);
                    const yld = tripYield(sourceTrip);
                    const wts = source.weights.length > 0 ? source.weights : parseLiveWeights(sourceTrip.liveWeights);
                    const avg =
                      source.avgLive != null
                        ? Math.round(source.avgLive)
                        : wts.length > 0
                          ? Math.round(wts.reduce((a, b) => a + b, 0) / wts.length)
                          : 0;
                    const pigCount = parseInt(sourceTrip.pigCount as unknown as string) || wts.length;
                    // Breeding Pigs that rode along. These are NOT part of
                    // pigCount/liveWeights/subAttributions — see tripAttachedBreeders
                    // in src/lib/pig.ts — so every inventory and FCR number on
                    // this page is computed from the feeder pigs alone.
                    const tripBreeders = resolveTripBreeders(t, breeders);
                    const totalAnimals = tripTotalAnimals(t, tripSourceOptions);
                    // Carcass yield uses only feeder-batch live and hanging weights.
                    // Added breeding pigs count as trip animals, while their own
                    // weights remain separate even when both are recorded.
                    const breederHang = tripBreederHangingTotal(t);
                    const totalHang = tripTotalHanging(t);
                    const tripForecast = processingForecastByTripId[t.id] || null;
                    const tripForecastAvg = tripForecast ? tripForecast.avg : null;
                    return (
                      <article key={t.id} data-pig-trip={t.id} className="pig-batch-trip">
                        <div className="pig-batch-trip__header">
                          <span className="pig-batch-trip__number" data-pig-trip-number={t.id}>
                            {processingTripOrdinals[`${g.id}:${t.id}`] != null
                              ? `Trip ${processingTripOrdinals[`${g.id}:${t.id}`]}`
                              : 'Trip'}
                          </span>
                          <div className="pig-batch-trip__identity">
                            <strong>{fmt(t.date)}</strong>
                            <div className="pig-batch-trip__summary">
                              <span data-pig-trip-batch-count={t.id}>
                                {tripBreeders.length > 0 ? 'Batch pigs: ' : ''}
                                {pigCount > 0 ? pigCount : 'No linked'}
                                {tripBreeders.length > 0 ? '' : ' pigs'}
                                {source.count === 0 && pigCount > 0 ? ' (stored)' : ''}
                              </span>
                              {tripBreeders.length > 0 && (
                                <span data-pig-trip-breeder-count={t.id}>
                                  Breeding Pigs: {tripBreeders.length} —{' '}
                                  {tripBreeders.map((b, bi) => (
                                    <React.Fragment key={b.breederId}>
                                      {bi > 0 ? ', ' : ''}
                                      {/* The link between the breeding animal and its
                                  trip, pointing at that animal's record. */}
                                      <Button
                                        data-pig-trip-breeder-link={b.breederId}
                                        onClick={() => navigate('/pig/sows/' + encodeURIComponent(b.breederId))}
                                      >
                                        {(b.sex || 'breeder').toLowerCase()} #{b.tag}
                                      </Button>
                                    </React.Fragment>
                                  ))}
                                </span>
                              )}
                              {tripBreeders.length > 0 && (
                                <span data-pig-trip-total-animals={t.id} style={{fontWeight: 700, color: 'var(--ink)'}}>
                                  Total trip animals: {totalAnimals}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="pig-batch-trip__actions">
                            {/* Quiet deep link to this trip's Processing record
                            (source key pig:<groupId>:<tripId>). */}
                            <Button
                              className="pig-batch-button pig-batch-button--processing"
                              data-pig-trip-processing-link={t.id}
                              onClick={() =>
                                navigateToProcessingRoute(navigate, processingSourceRoute('pig', g.id, t.id))
                              }
                            >
                              Processing
                            </Button>
                            <Button
                              className="pig-batch-button pig-batch-button--primary"
                              data-pig-trip-add-breeder={t.id}
                              onClick={() => openBreederPicker(t.id as string)}
                            >
                              <BatchActionIcon kind="add" /> Breeding Pig
                            </Button>
                            <Button
                              className="pig-batch-button pig-batch-button--icon"
                              aria-label={`Edit processing trip ${fmt(t.date)}`}
                              title="Edit trip"
                              onClick={() => {
                                setTripForm({
                                  date: t.date as string,
                                  pigCount,
                                  liveWeights: sourceTrip.liveWeights,
                                  hangingWeight: t.hangingWeight as number,
                                  notes: t.notes || '',
                                });
                                setEditTripId(t.id);
                                setActiveTripBatchId(g.id);
                              }}
                            >
                              <BatchActionIcon kind="edit" />
                            </Button>
                            <Button
                              className="pig-batch-button pig-batch-button--icon pig-batch-button--danger"
                              aria-label={`Delete processing trip ${fmt(t.date)}`}
                              title="Delete trip"
                              onClick={() => deleteTrip(g.id, t.id as string)}
                            >
                              <BatchActionIcon kind="delete" />
                            </Button>
                          </div>
                        </div>
                        <div className="pig-batch-trip__metrics">
                          <div>
                            <span className="pig-batch-kpi__label">Avg live weight</span>
                            <b>{avg > 0 ? `${avg} lbs` : '—'}</b>
                            <small>Total: {live > 0 ? `${Math.round(live).toLocaleString()} lbs` : '—'}</small>
                          </div>
                          <div data-pig-trip-forecast-compare={t.id}>
                            <span className="pig-batch-kpi__label">Forecast avg</span>
                            <b>{tripForecastAvg != null ? `${Math.round(tripForecastAvg)} lbs` : '—'}</b>
                          </div>
                          <div>
                            <span className="pig-batch-kpi__label">
                              {tripBreeders.length > 0 ? 'Batch hang weight' : 'Hang weight'}
                            </span>
                            <b>
                              {parseFloat(t.hangingWeight as string) > 0
                                ? `${parseFloat(t.hangingWeight as string).toLocaleString()} lbs`
                                : '—'}
                            </b>
                            {breederHang > 0 && (
                              <small data-pig-trip-total-hang={t.id}>Total hang: {totalHang} lbs</small>
                            )}
                          </div>
                          <div>
                            <span className="pig-batch-kpi__label">
                              {tripBreeders.length > 0 ? 'Batch carcass yield' : 'Yield'}
                            </span>
                            <b>{yld !== null ? `${yld}%` : '—'}</b>
                            {tripBreeders.length > 0 && <small>excludes added breeding pigs</small>}
                          </div>
                        </div>
                        {t.notes && <div className="pig-batch-trip__notes">{t.notes}</div>}
                        {/* Attached breeding pigs, each removable. Removing the
                          association never changes the animal's status. */}
                        {tripBreeders.length > 0 && (
                          <div
                            data-pig-trip-breeders={t.id}
                            style={{width: '100%', fontSize: 11, color: 'var(--ink-faint)', marginTop: 2}}
                          >
                            {tripBreeders.map((b) => (
                              <span key={b.breederId} style={{marginRight: 12}}>
                                {b.sex || 'Breeder'} #{b.tag} ·{' '}
                                {/* Her OWN carcass weight, from the processor's
                                  receipt. Never added into the trip's batch
                                  hanging weight. */}
                                <span data-pig-breeder-hang={b.breederId}>
                                  {b.hangingWeight != null ? `${b.hangingWeight} lbs hanging` : 'no hanging weight'}
                                </span>
                                {' · '}
                                <span data-pig-breeder-live={b.breederId}>
                                  {b.liveWeight != null ? `${b.liveWeight} lbs live` : 'live weight unknown'}
                                </span>
                                {b.breeder ? '' : ' · record removed'}
                                {breederWeightEdit &&
                                breederWeightEdit.tripId === t.id &&
                                breederWeightEdit.breederId === b.breederId ? (
                                  <>
                                    <input
                                      data-pig-breeder-hang-input={b.breederId}
                                      type="number"
                                      min="0"
                                      step="any"
                                      value={breederWeightDraft}
                                      onChange={(e) => setBreederWeightDraft(e.target.value)}
                                      placeholder="lbs hanging"
                                      style={{width: 90, marginLeft: 6, fontSize: 11}}
                                    />
                                    <Button
                                      data-pig-breeder-hang-save={b.breederId}
                                      disabled={breederPickerBusy}
                                      onClick={() => onSaveBreederWeight(g.id as string, t.id as string, b.breederId)}
                                    >
                                      Save
                                    </Button>
                                    <Button onClick={() => setBreederWeightEdit(null)}>Cancel</Button>
                                  </>
                                ) : (
                                  <Button
                                    data-pig-breeder-hang-edit={b.breederId}
                                    onClick={() => {
                                      setBreederTripNotice(null);
                                      setBreederWeightDraft(b.hangingWeight != null ? String(b.hangingWeight) : '');
                                      setBreederWeightEdit({tripId: t.id as string, breederId: b.breederId});
                                    }}
                                  >
                                    {b.hangingWeight != null ? 'Edit hanging wt' : 'Add hanging wt'}
                                  </Button>
                                )}
                                <Button
                                  data-pig-trip-remove-breeder={b.breederId}
                                  disabled={breederPickerBusy}
                                  onClick={() => onDetachBreeder(g.id as string, t.id as string, b.breederId, b.tag)}
                                >
                                  Remove
                                </Button>
                              </span>
                            ))}
                          </div>
                        )}
                        {breederTripNotice && breederPickerTripId === null && (
                          <div
                            data-pig-trip-breeder-notice={t.id}
                            style={{
                              width: '100%',
                              fontSize: 11,
                              marginTop: 2,
                              color: breederTripNotice.kind === 'error' ? 'var(--danger-ink)' : 'var(--ok-ink)',
                            }}
                          >
                            {breederTripNotice.message}
                          </div>
                        )}
                        {breederPickerTripId === t.id && (
                          <div
                            data-pig-trip-breeder-picker={t.id}
                            style={{
                              width: '100%',
                              marginTop: 6,
                              padding: 10,
                              border: '1px solid var(--border)',
                              borderRadius: 10,
                              background: 'var(--bg-card)',
                            }}
                          >
                            <div style={{fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginBottom: 4}}>
                              Add a breeding pig to this trip ({fmt(t.date)})
                            </div>
                            <div style={{fontSize: 11, color: 'var(--ink-faint)', marginBottom: 6}}>
                              Adding an animal records that she went on this trip. It does not change her status, and it
                              does not take a pig out of the feeder batch.
                            </div>
                            <input
                              value={breederPickerSearch}
                              onChange={(e) => setBreederPickerSearch(e.target.value)}
                              placeholder="Search tag, sex, breed, group, status..."
                              style={{...recordControl, marginBottom: 6}}
                            />
                            <div style={{maxHeight: 220, overflowY: 'auto'}}>
                              {(() => {
                                const q = breederPickerSearch.trim().toLowerCase();
                                // Sows, gilts and boars — including archived and
                                // already-processed animals, because correcting
                                // historical membership is a first-class use.
                                const candidates = (breeders || []).filter((b) =>
                                  ['sow', 'gilt', 'boar'].includes(String(b.sex || '').toLowerCase()),
                                );
                                const matches = candidates.filter((b) => {
                                  if (!q) return true;
                                  return [b.tag, b.sex, b.breed, b.group, b.status, b.origin]
                                    .map((v) => String(v ?? '').toLowerCase())
                                    .join(' ')
                                    .includes(q);
                                });
                                if (matches.length === 0) {
                                  return (
                                    <div style={{fontSize: 11, color: 'var(--ink-faint)'}}>
                                      No breeding pigs match that search.
                                    </div>
                                  );
                                }
                                return matches.slice(0, 40).map((b) => {
                                  const id = String(b.id ?? '');
                                  const held = findBreederTripAttachment(feederGroups, id);
                                  const wt = breederLiveWeight(b as never);
                                  return (
                                    <div
                                      key={id}
                                      data-pig-breeder-option={id}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        flexWrap: 'wrap',
                                        padding: '5px 0',
                                        borderBottom: '1px solid var(--divider)',
                                        fontSize: 11,
                                      }}
                                    >
                                      <span style={{fontWeight: 700, color: 'var(--ink)', minWidth: 54}}>
                                        #{String(b.tag ?? '')}
                                      </span>
                                      <span style={{color: 'var(--text-primary)'}}>{b.sex}</span>
                                      {b.breed ? (
                                        <span style={{color: 'var(--ink-faint)'}}>{String(b.breed)}</span>
                                      ) : null}
                                      {b.group ? (
                                        <span style={{color: 'var(--ink-faint)'}}>Group {String(b.group)}</span>
                                      ) : null}
                                      {b.birthDate ? (
                                        <span style={{color: 'var(--ink-faint)'}}>
                                          born {fmtS(b.birthDate as string)}
                                        </span>
                                      ) : null}
                                      <span style={{color: 'var(--ink-faint)'}}>
                                        {wt != null ? `${wt} lbs` : 'no weight'}
                                      </span>
                                      <span style={{color: 'var(--ink-faint)'}}>{String(b.status ?? '')}</span>
                                      {b.archived ? <span style={{color: 'var(--ink-faint)'}}>archived</span> : null}
                                      {held ? (
                                        <span data-pig-breeder-held={id} style={{color: 'var(--warn-ink)'}}>
                                          already on {held.batchName}
                                          {held.tripDate ? ' · ' + fmtS(held.tripDate) : ''}
                                        </span>
                                      ) : (
                                        <Button
                                          data-pig-breeder-attach={id}
                                          disabled={breederPickerBusy}
                                          onClick={() =>
                                            onAttachBreeder(g.id as string, t.id as string, id, String(b.tag ?? id))
                                          }
                                          style={{marginLeft: 'auto'}}
                                        >
                                          {breederPickerBusy ? 'Adding…' : 'Add to this trip'}
                                        </Button>
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                            {breederTripNotice && (
                              <div
                                data-pig-trip-breeder-picker-notice={t.id}
                                style={{
                                  fontSize: 11,
                                  marginTop: 6,
                                  color: breederTripNotice.kind === 'error' ? 'var(--danger-ink)' : 'var(--ok-ink)',
                                }}
                              >
                                {breederTripNotice.message}
                              </div>
                            )}
                            <Button
                              onClick={() => {
                                setBreederPickerTripId(null);
                                setBreederTripNotice(null);
                              }}
                              style={{marginTop: 6}}
                            >
                              Close
                            </Button>
                          </div>
                        )}
                        {wts.length > 0 && (
                          <div className="pig-batch-trip__weights">
                            <strong>Weights</strong>
                            <div>
                              {wts.map((weight, index) => (
                                <span key={index}>{weight}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {source.count === 0 && (
                          <div
                            data-pig-trip-source-missing={t.id}
                            style={{width: '100%', fontSize: 11, color: 'var(--warn-ink)', marginTop: 2}}
                          >
                            No linked weigh-in entries. Showing stored legacy weights for this existing trip.
                          </div>
                        )}
                        {(() => {
                          const counts = tripSourceCounts(t.id);
                          const keys = Object.keys(counts);
                          if (keys.length === 0) return null;
                          return (
                            <div className="pig-batch-trip__from">
                              <strong>From:</strong> {keys.map((k) => k + ' (' + counts[k] + ')').join(', ')}
                            </div>
                          );
                        })()}
                      </article>
                    );
                  })}
                </div>
              </section>

              {/* Mortality summary (expandable) */}
              {(g.pigMortalities || []).length > 0 &&
                (() => {
                  const morts = g.pigMortalities || [];
                  const total = morts.reduce((s, m) => s + (parseInt(m.count as string) || 0), 0);

                  return (
                    <Collapsible
                      label="Mortality"
                      count={morts.length}
                      summary={total + ' deaths recorded'}
                      stateKey={'pig-mortality:' + g.id}
                      defaultOpen={false}
                    >
                      <div style={{marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4}}>
                        {[...morts].reverse().map((m) => (
                          <div
                            key={m.id}
                            style={{
                              display: 'flex',
                              gap: 10,
                              alignItems: 'center',
                              padding: '4px 8px',
                              background: 'var(--surface)',
                              borderRadius: 10,
                              border: '1px solid var(--border)',
                              flexWrap: 'wrap',
                            }}
                          >
                            <span style={{color: 'var(--ink)', minWidth: 90, fontWeight: 600}}>{fmt(m.date)}</span>
                            <span style={{color: 'var(--danger-ink)', fontWeight: 700, minWidth: 32}}>{m.count}</span>
                            <span style={{color: 'var(--ink-muted)', minWidth: 120}}>
                              {m.sub_batch_name || 'Whole batch'}
                            </span>
                            {m.comment && (
                              <span style={{color: 'var(--ink)', fontStyle: 'italic', flex: 1, minWidth: 120}}>
                                {m.comment}
                              </span>
                            )}
                            <span style={{color: 'var(--ink-faint)', fontSize: 10, marginLeft: 'auto'}}>
                              {m.team_member}
                            </span>
                            <Button onClick={() => deleteMortality(g.id, m.id as string)} title="Delete">
                              {'×'}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </Collapsible>
                  );
                })()}

              {/* Cycle info footer */}
              {tl && (
                <div
                  style={{
                    padding: '6px 16px',
                    background: 'var(--surface-2)',
                    borderTop: '1px solid var(--border)',
                    fontSize: 11,
                    color: 'var(--ink-faint)',
                  }}
                >
                  {cycleLabel(cycle, cycleSeqMap)} · Farrowing: {fmtS(tl.farrowingStart)} → {fmtS(tl.farrowingEnd)} ·{' '}
                  {boarNames.boar1}: {(cycle as PigBreedingCycle).boar1Tags || '—'} · {boarNames.boar2}:{' '}
                  {(cycle as PigBreedingCycle).boar2Tags || '—'}
                </div>
              )}
            </div>
          );
        })}
        <RecordCollaborationSection
          sb={sb}
          authState={authState}
          entityType="pig.batch"
          entityId={group.id}
          entityLabel={group.batchName}
          fullYearDates
        />
        {/* R24: Delete is a danger action at the foot, below Comments and
            Activity — the same deleteFeeder and confirmation the editor's
            footer carried. */}
        <Button
          variant="danger"
          type="button"
          disabled={feederEditor.busy}
          onClick={() => feederEditor.remove(group.id)}
          data-pig-batch-delete={group.id}
          style={{marginTop: 12}}
        >
          Delete Batch
        </Button>
      </RecordPageBody>
    </RecordPageFrame>
  );
}
